import { useCallback } from 'react';
// Build a per-site-key script (supports multiple distinct keys on one page) using ?render=<siteKey>
// to force key initialization at script load time (eliminates lazy init race).
const buildScriptId = (siteKey, isEnterprise) => `recaptcha-${isEnterprise ? 'enterprise' : 'v3'}-${siteKey}`;
const buildScriptSrc = (siteKey, isEnterprise, language) => {
    const base = isEnterprise
        ? 'https://www.google.com/recaptcha/enterprise.js'
        : 'https://www.recaptcha.net/recaptcha/api.js';
    const params = new URLSearchParams();
    params.set('render', siteKey);
    if (language) {
        params.set('hl', language);
    }
    return `${base}?${params.toString()}`;
};
// Cache promises per unique script id (siteKey + variant)
const recaptchaKeyLoadPromises = {};
/**
 * Clears cached script load promises so the next call will attempt a fresh injection. Use if you:
 *  - Need to change language (first successfully loaded language is sticky until cleared)
 *  - Want to intentionally re-attempt after a previous network failure
 *  - Are performing a hard client-side locale switch without full page reload
 *
 * Prefer not to call this during normal flows; reCAPTCHA scripts are designed to stay resident.
 */
export const clearRecaptchaCache = () => {
    Object.keys(recaptchaKeyLoadPromises).forEach((k) => delete recaptchaKeyLoadPromises[k]);
};
// Ensure a script for this exact site key + variant is loaded.
const ensureRecaptchaScriptLoaded = (siteKey, isEnterprise, language) => {
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }
    const scriptId = buildScriptId(siteKey, isEnterprise);
    const existing = recaptchaKeyLoadPromises[scriptId];
    if (typeof existing !== 'undefined') {
        return existing;
    }
    if (document.getElementById(scriptId)) {
        return Promise.resolve(); // already present
    }
    const promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = buildScriptSrc(siteKey, isEnterprise, language);
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            delete recaptchaKeyLoadPromises[scriptId];
            script.remove();
            reject(new Error(`Failed to load reCAPTCHA script for site key: ${siteKey}`));
        };
        document.head.appendChild(script);
    });
    recaptchaKeyLoadPromises[scriptId] = promise;
    return promise;
};
/**
 * Waits until the appropriate grecaptcha client exposes an `execute` function or rejects after the
 * configured attempts (default ~5s: 50 x 100ms). Also attaches to grecaptcha.ready() when available
 * to fast-path readiness. Purely internal; callers should use `getToken`.
 */
const waitForGrecaptcha = (isEnterprise, maxAttempts = 50, intervalMs = 100) => {
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        const win = window;
        const getClient = () => (isEnterprise ? win.grecaptcha?.enterprise : win.grecaptcha);
        let attempts = 0;
        // Attempts to resolve immediately if execute is available; returns true when resolved.
        const resolveIfReady = () => {
            const g = getClient();
            if (g?.execute) {
                resolve();
                return true;
            }
            return false;
        };
        // Fast-path: grecaptcha already present.
        if (resolveIfReady()) {
            return;
        }
        // Poll until execute is available or timeout.
        const intervalId = setInterval(() => {
            attempts += 1;
            if (resolveIfReady()) {
                clearInterval(intervalId);
            }
            else if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                reject(new Error('grecaptcha not ready'));
            }
        }, intervalMs);
        try {
            // Attach to the appropriate ready() callback for the variant being used.
            // For enterprise we prefer enterprise.ready() but also fall back to top-level ready()
            // because some environments expose only the latter. For standard we only use top-level.
            const safeReady = (obj) => {
                try {
                    if (obj && typeof obj.ready === 'function') {
                        obj.ready(() => {
                            if (resolveIfReady()) {
                                clearInterval(intervalId);
                            }
                        });
                    }
                }
                catch {
                    /* ignore */
                }
            };
            if (isEnterprise) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                safeReady(win.grecaptcha?.enterprise);
            }
            else {
                safeReady(win.grecaptcha);
            }
        }
        catch {
            // Intentionally ignore errors thrown while checking for grecaptcha.ready.
            // If access throws (e.g. partially stubbed object or transient state), we fall back
            // to the polling loop above; no need to surface noise to consumers.
        }
    });
};
/**
 * Google reCAPTCHA v3 / Enterprise hook
 *
 * Design:
 *  - Per key script: each (siteKey, variant) pair injects its own script with ?render=siteKey (eager init).
 *  - Lazy: script loads only after first priming (or implicit on first getToken call).
 *  - Idempotent per key: concurrent callers for same key share single in‑flight promise.
 *  - Stateless tokens: no prefetch; each `getToken` call obtains a fresh token for the supplied action.
 *  - Resilient: failures return null (never throw) and are logged to console.error.
 *  - Language: first successful load fixes the language until `clearRecaptchaCache()` is invoked.
 *
 * Parameters:
 *  @param recaptchaSiteKey Public site key (only sent during execute, not script URL).
 *  @param isEnterprise When true loads & executes the Enterprise variant.
 *  @param language Optional locale code appended as `hl` query; first load wins.
 *
 * Returns API:
 *  - enableRecaptcha(): void — fire-and-forget priming (safe in event handlers).
 *  - enableRecaptchaAsync(): Promise<void> — awaitable priming (mainly tests / orchestration).
 *  - getToken(action?): Promise<string|null> — fetch fresh token; ensures load & readiness.
 */
export const useRecaptcha = (recaptchaSiteKey, isEnterprise = false, language) => {
    // Async (awaitable) variant
    const enableRecaptchaAsync = useCallback(() => {
        return ensureRecaptchaScriptLoaded(recaptchaSiteKey, isEnterprise, language);
    }, [recaptchaSiteKey, isEnterprise, language]);
    // Void wrapper for event handlers – deliberately discards the promise to satisfy eslint rule
    // that forbids returning a Promise from a void event handler.
    const enableRecaptcha = useCallback(() => {
        void enableRecaptchaAsync();
    }, [enableRecaptchaAsync]);
    const getToken = useCallback(async (action = 'submit') => {
        if (typeof window === 'undefined') {
            return null;
        }
        try {
            // Ensure script injected (idempotent) & wait for grecaptcha to expose execute.
            await ensureRecaptchaScriptLoaded(recaptchaSiteKey, isEnterprise, language);
            await waitForGrecaptcha(isEnterprise);
            const recaptchaWindow = window;
            const grecaptcha = isEnterprise ? recaptchaWindow.grecaptcha?.enterprise : recaptchaWindow.grecaptcha;
            if (!grecaptcha?.execute) {
                throw new Error('grecaptcha.execute not available after load');
            }
            // Execute with provided site key & action to obtain a fresh token each call.
            const token = await grecaptcha.execute(recaptchaSiteKey, { action });
            return token;
        }
        catch (err) {
            // Swallow errors and surface null so callers can decide on fallback UX.
            console.error('Failed to get reCAPTCHA token', err);
            return null;
        }
    }, [recaptchaSiteKey, isEnterprise, language]);
    return { enableRecaptcha, enableRecaptchaAsync, getToken };
};
